import React, { useState, useEffect } from 'react';
import { useAuth, useTheme, COLOR_SCHEMES } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { 
  Settings, Target, Users, Heart, DollarSign, AlertTriangle, 
  History, Save, RefreshCw, Shield, CheckCircle2, Clock, RotateCcw,
  Download, Upload, Database, HardDrive, FileJson, Loader2, Palette, Moon, Sun
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Section Header Component
const SectionHeader = ({ icon: Icon, title, description }) => (
  <div className="flex items-center gap-3 mb-4">
    <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
      <Icon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
    </div>
    <div>
      <h3 className="font-semibold text-lg">{title}</h3>
      {description && <p className="text-sm text-muted-foreground">{description}</p>}
    </div>
  </div>
);

// Config Field Component
const ConfigField = ({ label, value, onChange, type = "number", hint, disabled }) => (
  <div className="space-y-2">
    <Label className="text-sm font-medium">{label}</Label>
    <Input 
      type={type}
      value={value ?? ''}
      onChange={(e) => onChange(type === "number" ? parseFloat(e.target.value) || 0 : e.target.value)}
      className="h-9"
      disabled={disabled}
      data-testid={`config-${label.toLowerCase().replace(/\s+/g, '-')}`}
    />
    {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
  </div>
);

export default function SystemConfiguration() {
  const { token, user } = useAuth();
  const { theme, setTheme, colorScheme, setColorScheme, colorSchemes } = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [activeTab, setActiveTab] = useState('strategic');
  const [config, setConfig] = useState({});
  const [auditHistory, setAuditHistory] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [dataSummary, setDataSummary] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [seeding, setSeeding] = useState(false);

  // Check if user can edit (admin or super_admin only)
  const canEdit = user?.role === 'super_admin' || user?.role === 'admin';
  const isSuperAdmin = user?.role === 'super_admin';

  // Fetch all configuration
  const fetchConfig = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/system-config/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
      }
    } catch (error) {
      console.error('Error fetching config:', error);
      toast.error('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  // Fetch audit history
  const fetchAuditHistory = async () => {
    try {
      const res = await fetch(`${API_URL}/api/system-config/audit/history?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAuditHistory(data.history || []);
      }
    } catch (error) {
      console.error('Error fetching audit history:', error);
    }
  };

  useEffect(() => {
    fetchConfig();
    fetchAuditHistory();
    fetchDataSummary();
  }, [token]);

  // Save configuration
  const saveConfig = async (configType) => {
    if (!canEdit) {
      toast.error('You do not have permission to modify configuration');
      return;
    }
    
    try {
      setSaving(true);
      const res = await fetch(`${API_URL}/api/system-config/${configType}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(config[configType])
      });
      
      if (res.ok) {
        toast.success(`${configType.charAt(0).toUpperCase() + configType.slice(1)} configuration saved`);
        setHasChanges(false);
        fetchAuditHistory();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save configuration');
      }
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  // Reset single config type to defaults
  const resetConfigToDefaults = async (configType) => {
    if (!canEdit) {
      toast.error('You do not have permission to reset configuration');
      return;
    }
    
    try {
      setResetting(true);
      const res = await fetch(`${API_URL}/api/system-config/reset/${configType}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        // Update local state with default values
        setConfig(prev => ({
          ...prev,
          [configType]: data.values
        }));
        toast.success(`${configType.charAt(0).toUpperCase() + configType.slice(1)} configuration reset to defaults`);
        setHasChanges(false);
        fetchAuditHistory();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to reset configuration');
      }
    } catch (error) {
      console.error('Error resetting config:', error);
      toast.error('Failed to reset configuration');
    } finally {
      setResetting(false);
    }
  };

  // Reset ALL configs to defaults (super admin only)
  const resetAllConfigsToDefaults = async () => {
    if (!isSuperAdmin) {
      toast.error('Only super admin can reset all configurations');
      return;
    }
    
    try {
      setResetting(true);
      const res = await fetch(`${API_URL}/api/system-config/reset-all`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (res.ok) {
        toast.success('All configurations reset to defaults');
        setHasChanges(false);
        await fetchConfig(); // Reload all configs
        fetchAuditHistory();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to reset all configurations');
      }
    } catch (error) {
      console.error('Error resetting all configs:', error);
      toast.error('Failed to reset all configurations');
    } finally {
      setResetting(false);
    }
  };

  // Fetch data summary for backup tab
  const fetchDataSummary = async () => {
    try {
      const res = await fetch(`${API_URL}/api/system-config/backup/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDataSummary(data);
      }
    } catch (error) {
      console.error('Error fetching data summary:', error);
    }
  };

  // Export all data
  const exportAllData = async () => {
    try {
      setExporting(true);
      const res = await fetch(`${API_URL}/api/system-config/backup/export`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        
        // Create and download JSON file
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `hospice-carelens-backup-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        toast.success(`Exported ${data.export_info.total_records} records successfully`);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to export data');
      }
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  // Import data from file
  const handleImportFile = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    try {
      setImporting(true);
      const text = await file.text();
      const backupData = JSON.parse(text);
      
      const res = await fetch(`${API_URL}/api/system-config/backup/import?merge_mode=replace`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(backupData)
      });
      
      if (res.ok) {
        const result = await res.json();
        toast.success(`Imported ${result.total_records_imported} records successfully`);
        await fetchConfig();
        await fetchDataSummary();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to import data');
      }
    } catch (error) {
      console.error('Error importing data:', error);
      toast.error('Invalid backup file format');
    } finally {
      setImporting(false);
      event.target.value = ''; // Reset file input
    }
  };

  // Seed production data
  const seedProductionData = async () => {
    try {
      setSeeding(true);
      const res = await fetch(`${API_URL}/api/system-config/seed/production`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (res.ok) {
        const result = await res.json();
        if (result.seeded_collections.length > 0) {
          toast.success(`Seeded ${result.seeded_collections.length} collections with baseline data`);
        } else {
          toast.info('All collections already have data - no seeding needed');
        }
        await fetchConfig();
        await fetchDataSummary();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to seed data');
      }
    } catch (error) {
      console.error('Error seeding data:', error);
      toast.error('Failed to seed production data');
    } finally {
      setSeeding(false);
    }
  };

  // Update local config state
  const updateConfig = (section, key, value) => {
    setConfig(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value
      }
    }));
    setHasChanges(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">
            System Configuration
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage all system-wide targets, thresholds, and business rules
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant={config.source === 'database' ? 'default' : 'secondary'}>
            {config.source === 'database' ? 'Database' : 'Defaults'}
          </Badge>
          {!canEdit && (
            <Badge variant="outline" className="text-amber-600">
              <Shield className="h-3 w-3 mr-1" /> View Only
            </Badge>
          )}
          {isSuperAdmin && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="text-amber-600 border-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950"
                  disabled={resetting}
                  data-testid="reset-all-defaults-btn"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Reset All to Defaults
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Reset All Configuration?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will reset ALL configuration categories (Strategic, Workforce, Clinical, Financial, Alerts, Phases, Validation) to their default values. This action cannot be undone but will be logged in the audit history.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={resetAllConfigsToDefaults}
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    Reset All to Defaults
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-5 lg:grid-cols-9 h-auto">
          <TabsTrigger value="strategic" className="flex items-center gap-2">
            <Target className="h-4 w-4" />
            <span className="hidden sm:inline">Strategic</span>
          </TabsTrigger>
          <TabsTrigger value="workforce" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            <span className="hidden sm:inline">Workforce</span>
          </TabsTrigger>
          <TabsTrigger value="clinical" className="flex items-center gap-2">
            <Heart className="h-4 w-4" />
            <span className="hidden sm:inline">Clinical</span>
          </TabsTrigger>
          <TabsTrigger value="financial" className="flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            <span className="hidden sm:inline">Financial</span>
          </TabsTrigger>
          <TabsTrigger value="alerts" className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            <span className="hidden sm:inline">Alerts</span>
          </TabsTrigger>
          <TabsTrigger value="phases" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            <span className="hidden sm:inline">Phases</span>
          </TabsTrigger>
          <TabsTrigger value="backup" className="flex items-center gap-2">
            <Database className="h-4 w-4" />
            <span className="hidden sm:inline">Backup</span>
          </TabsTrigger>
          <TabsTrigger value="theme" className="flex items-center gap-2">
            <Palette className="h-4 w-4" />
            <span className="hidden sm:inline">Theme</span>
          </TabsTrigger>
          <TabsTrigger value="audit" className="flex items-center gap-2">
            <History className="h-4 w-4" />
            <span className="hidden sm:inline">Audit Log</span>
          </TabsTrigger>
        </TabsList>

        {/* Strategic Targets */}
        <TabsContent value="strategic">
          <Card>
            <CardHeader>
              <SectionHeader 
                icon={Target} 
                title="Strategic Targets" 
                description="5-Year strategic plan goals and milestones"
              />
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <ConfigField 
                  label="Baseline ADC"
                  value={config.strategic?.baseline_adc}
                  onChange={(v) => updateConfig('strategic', 'baseline_adc', v)}
                  hint="Starting average daily census"
                  disabled={!canEdit}
                />
                <ConfigField 
                  label="Target ADC"
                  value={config.strategic?.target_adc}
                  onChange={(v) => updateConfig('strategic', 'target_adc', v)}
                  hint="5-year goal ADC"
                  disabled={!canEdit}
                />
                <ConfigField 
                  label="Plan Start Year"
                  value={config.strategic?.plan_start_year}
                  onChange={(v) => updateConfig('strategic', 'plan_start_year', v)}
                  disabled={!canEdit}
                />
                <ConfigField 
                  label="Plan End Year"
                  value={config.strategic?.plan_end_year}
                  onChange={(v) => updateConfig('strategic', 'plan_end_year', v)}
                  disabled={!canEdit}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <ConfigField 
                  label="Weekly Admissions Target"
                  value={config.strategic?.weekly_admissions_target}
                  onChange={(v) => updateConfig('strategic', 'weekly_admissions_target', v)}
                  disabled={!canEdit}
                />
                <ConfigField 
                  label="Discharge Rate Target"
                  value={config.strategic?.discharge_rate_target}
                  onChange={(v) => updateConfig('strategic', 'discharge_rate_target', v)}
                  hint="As decimal (0.06 = 6%)"
                  disabled={!canEdit}
                />
                <ConfigField 
                  label="ALOS Target (days)"
                  value={config.strategic?.alos_target}
                  onChange={(v) => updateConfig('strategic', 'alos_target', v)}
                  disabled={!canEdit}
                />
                <ConfigField 
                  label="Referral Conversion Target"
                  value={config.strategic?.referral_conversion_target}
                  onChange={(v) => updateConfig('strategic', 'referral_conversion_target', v)}
                  hint="As decimal (0.80 = 80%)"
                  disabled={!canEdit}
                />
              </div>
              {canEdit && (
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" disabled={resetting} data-testid="reset-strategic-btn">
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Reset to Defaults
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Reset Strategic Targets?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will reset all strategic targets to their default values. This action will be logged in the audit history.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => resetConfigToDefaults('strategic')}>
                          Reset to Defaults
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  <Button onClick={() => saveConfig('strategic')} disabled={saving}>
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Strategic Targets'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Workforce Targets */}
        <TabsContent value="workforce">
          <Card>
            <CardHeader>
              <SectionHeader 
                icon={Users} 
                title="Workforce Targets" 
                description="RNCM caseload and staffing thresholds"
              />
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <ConfigField 
                  label="RNCM Caseload Target"
                  value={config.workforce?.rncm_caseload_target}
                  onChange={(v) => updateConfig('workforce', 'rncm_caseload_target', v)}
                  hint="Optimal patients per RNCM"
                  disabled={!canEdit}
                />
                <ConfigField 
                  label="Caseload Warning Threshold"
                  value={config.workforce?.rncm_caseload_warning}
                  onChange={(v) => updateConfig('workforce', 'rncm_caseload_warning', v)}
                  disabled={!canEdit}
                />
                <ConfigField 
                  label="Caseload Critical Threshold"
                  value={config.workforce?.rncm_caseload_critical}
                  onChange={(v) => updateConfig('workforce', 'rncm_caseload_critical', v)}
                  disabled={!canEdit}
                />
                <ConfigField 
                  label="Caseload Emergency Threshold"
                  value={config.workforce?.rncm_caseload_emergency}
                  onChange={(v) => updateConfig('workforce', 'rncm_caseload_emergency', v)}
                  disabled={!canEdit}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <ConfigField 
                  label="Staff Utilization Target"
                  value={config.workforce?.staff_utilization_target}
                  onChange={(v) => updateConfig('workforce', 'staff_utilization_target', v)}
                  hint="As decimal (0.85 = 85%)"
                  disabled={!canEdit}
                />
                <ConfigField 
                  label="Turnover Rate Target"
                  value={config.workforce?.turnover_rate_target}
                  onChange={(v) => updateConfig('workforce', 'turnover_rate_target', v)}
                  hint="As decimal (0.10 = 10%)"
                  disabled={!canEdit}
                />
                <ConfigField 
                  label="PRN Utilization Target"
                  value={config.workforce?.prn_utilization_target}
                  onChange={(v) => updateConfig('workforce', 'prn_utilization_target', v)}
                  hint="As decimal (0.10 = 10%)"
                  disabled={!canEdit}
                />
                <ConfigField 
                  label="PRN Utilization Warning"
                  value={config.workforce?.prn_utilization_warning}
                  onChange={(v) => updateConfig('workforce', 'prn_utilization_warning', v)}
                  hint="As decimal (0.12 = 12%)"
                  disabled={!canEdit}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <ConfigField 
                  label="Optimal Visits Per Week"
                  value={config.workforce?.optimal_visits_per_week}
                  onChange={(v) => updateConfig('workforce', 'optimal_visits_per_week', v)}
                  disabled={!canEdit}
                />
                <ConfigField 
                  label="RNCM Final Target"
                  value={config.workforce?.rncm_final_target}
                  onChange={(v) => updateConfig('workforce', 'rncm_final_target', v)}
                  hint="Target number of RNCMs"
                  disabled={!canEdit}
                />
              </div>
              {canEdit && (
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" disabled={resetting} data-testid="reset-workforce-btn">
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Reset to Defaults
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Reset Workforce Targets?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will reset all workforce targets to their default values. This action will be logged in the audit history.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => resetConfigToDefaults('workforce')}>
                          Reset to Defaults
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  <Button onClick={() => saveConfig('workforce')} disabled={saving}>
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Workforce Targets'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Clinical Targets */}
        <TabsContent value="clinical">
          <Card>
            <CardHeader>
              <SectionHeader 
                icon={Heart} 
                title="Clinical Targets" 
                description="Quality metrics and clinical performance thresholds"
              />
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <ConfigField 
                  label="Pain Control Target"
                  value={config.clinical?.pain_control_target}
                  onChange={(v) => updateConfig('clinical', 'pain_control_target', v)}
                  hint="As decimal (0.95 = 95%)"
                  disabled={!canEdit}
                />
                <ConfigField 
                  label="Pain Control Warning"
                  value={config.clinical?.pain_control_warning}
                  onChange={(v) => updateConfig('clinical', 'pain_control_warning', v)}
                  hint="As decimal (0.90 = 90%)"
                  disabled={!canEdit}
                />
                <ConfigField 
                  label="Hospitalization Target"
                  value={config.clinical?.hospitalization_target}
                  onChange={(v) => updateConfig('clinical', 'hospitalization_target', v)}
                  hint="As decimal (0.03 = 3%)"
                  disabled={!canEdit}
                />
                <ConfigField 
                  label="Hospitalization Warning"
                  value={config.clinical?.hospitalization_warning}
                  onChange={(v) => updateConfig('clinical', 'hospitalization_warning', v)}
                  hint="As decimal (0.05 = 5%)"
                  disabled={!canEdit}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <ConfigField 
                  label="CAHPS Target"
                  value={config.clinical?.cahps_target}
                  onChange={(v) => updateConfig('clinical', 'cahps_target', v)}
                  hint="Target score (e.g., 93)"
                  disabled={!canEdit}
                />
                <ConfigField 
                  label="Compliance Target"
                  value={config.clinical?.compliance_target}
                  onChange={(v) => updateConfig('clinical', 'compliance_target', v)}
                  hint="As decimal (0.97 = 97%)"
                  disabled={!canEdit}
                />
                <ConfigField 
                  label="Live Discharge Target"
                  value={config.clinical?.live_discharge_target}
                  onChange={(v) => updateConfig('clinical', 'live_discharge_target', v)}
                  hint="As decimal (0.03 = 3%)"
                  disabled={!canEdit}
                />
                <ConfigField 
                  label="Missed Visits Target"
                  value={config.clinical?.missed_visits_target}
                  onChange={(v) => updateConfig('clinical', 'missed_visits_target', v)}
                  hint="As decimal (0.05 = 5%)"
                  disabled={!canEdit}
                />
              </div>
              {canEdit && (
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" disabled={resetting} data-testid="reset-clinical-btn">
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Reset to Defaults
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Reset Clinical Targets?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will reset all clinical targets to their default values. This action will be logged in the audit history.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => resetConfigToDefaults('clinical')}>
                          Reset to Defaults
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  <Button onClick={() => saveConfig('clinical')} disabled={saving}>
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Clinical Targets'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Financial Targets */}
        <TabsContent value="financial">
          <Card>
            <CardHeader>
              <SectionHeader 
                icon={DollarSign} 
                title="Financial Targets" 
                description="Revenue, cost, and margin thresholds"
              />
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <ConfigField 
                  label="Monthly Revenue Target ($)"
                  value={config.financial?.monthly_revenue_target}
                  onChange={(v) => updateConfig('financial', 'monthly_revenue_target', v)}
                  disabled={!canEdit}
                />
                <ConfigField 
                  label="Cost Per Episode Target ($)"
                  value={config.financial?.cost_per_episode_target}
                  onChange={(v) => updateConfig('financial', 'cost_per_episode_target', v)}
                  disabled={!canEdit}
                />
                <ConfigField 
                  label="Net Margin Target"
                  value={config.financial?.net_margin_target}
                  onChange={(v) => updateConfig('financial', 'net_margin_target', v)}
                  hint="As decimal (0.12 = 12%)"
                  disabled={!canEdit}
                />
                <ConfigField 
                  label="Bad Debt Target"
                  value={config.financial?.bad_debt_target}
                  onChange={(v) => updateConfig('financial', 'bad_debt_target', v)}
                  hint="As decimal (0.02 = 2%)"
                  disabled={!canEdit}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <ConfigField 
                  label="AR 90 Days Warning"
                  value={config.financial?.ar_90_days_warning}
                  onChange={(v) => updateConfig('financial', 'ar_90_days_warning', v)}
                  hint="As decimal (0.10 = 10%)"
                  disabled={!canEdit}
                />
              </div>
              {canEdit && (
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" disabled={resetting} data-testid="reset-financial-btn">
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Reset to Defaults
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Reset Financial Targets?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will reset all financial targets to their default values. This action will be logged in the audit history.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => resetConfigToDefaults('financial')}>
                          Reset to Defaults
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  <Button onClick={() => saveConfig('financial')} disabled={saving}>
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Financial Targets'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Alert Thresholds */}
        <TabsContent value="alerts">
          <Card>
            <CardHeader>
              <SectionHeader 
                icon={AlertTriangle} 
                title="Alert Thresholds" 
                description="Configure when alerts and notifications are triggered"
              />
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <ConfigField 
                  label="Warning Threshold (%)"
                  value={config.alerts?.warning_threshold}
                  onChange={(v) => updateConfig('alerts', 'warning_threshold', v)}
                  hint="Score below this triggers warning"
                  disabled={!canEdit}
                />
                <ConfigField 
                  label="Critical Threshold (%)"
                  value={config.alerts?.critical_threshold}
                  onChange={(v) => updateConfig('alerts', 'critical_threshold', v)}
                  hint="Score below this triggers critical alert"
                  disabled={!canEdit}
                />
                <ConfigField 
                  label="Check Frequency (hours)"
                  value={config.alerts?.check_frequency_hours}
                  onChange={(v) => updateConfig('alerts', 'check_frequency_hours', v)}
                  disabled={!canEdit}
                />
              </div>
              {canEdit && (
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" disabled={resetting} data-testid="reset-alerts-btn">
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Reset to Defaults
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Reset Alert Thresholds?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will reset all alert thresholds to their default values. This action will be logged in the audit history.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => resetConfigToDefaults('alerts')}>
                          Reset to Defaults
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  <Button onClick={() => saveConfig('alerts')} disabled={saving}>
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Alert Thresholds'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Phase Targets */}
        <TabsContent value="phases">
          <Card>
            <CardHeader>
              <SectionHeader 
                icon={Clock} 
                title="Phase Targets" 
                description="5-year strategic plan phase milestones"
              />
            </CardHeader>
            <CardContent className="space-y-6">
              {Object.entries(config.phases || {}).map(([phaseKey, phase]) => (
                <div key={phaseKey} className="p-4 border rounded-lg space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-lg">{phase.name}</h4>
                    <Badge>Year {phase.year}</Badge>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <ConfigField 
                      label="ADC Target"
                      value={phase.adc_target}
                      onChange={(v) => {
                        setConfig(prev => ({
                          ...prev,
                          phases: {
                            ...prev.phases,
                            [phaseKey]: { ...phase, adc_target: v }
                          }
                        }));
                        setHasChanges(true);
                      }}
                      disabled={!canEdit}
                    />
                    <ConfigField 
                      label="Weekly Admissions"
                      value={phase.weekly_admissions}
                      onChange={(v) => {
                        setConfig(prev => ({
                          ...prev,
                          phases: {
                            ...prev.phases,
                            [phaseKey]: { ...phase, weekly_admissions: v }
                          }
                        }));
                        setHasChanges(true);
                      }}
                      disabled={!canEdit}
                    />
                  </div>
                </div>
              ))}
              {canEdit && (
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" disabled={resetting} data-testid="reset-phases-btn">
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Reset to Defaults
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Reset Phase Targets?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will reset all phase targets to their default values. This action will be logged in the audit history.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => resetConfigToDefaults('phases')}>
                          Reset to Defaults
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  <Button onClick={() => saveConfig('phases')} disabled={saving}>
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Phase Targets'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Backup & Export */}
        <TabsContent value="backup">
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Data Summary Card */}
            <Card>
              <CardHeader>
                <SectionHeader 
                  icon={HardDrive} 
                  title="Data Summary" 
                  description="Current state of all system data"
                />
              </CardHeader>
              <CardContent>
                {dataSummary ? (
                  <div className="space-y-4">
                    {/* Health Score */}
                    <div className="p-4 rounded-lg bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-950/30 dark:to-cyan-950/30">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">Data Health Score</span>
                        <Badge variant={dataSummary.data_health?.status === 'healthy' ? 'default' : 
                                       dataSummary.data_health?.status === 'needs_data' ? 'secondary' : 'destructive'}>
                          {dataSummary.data_health?.status?.toUpperCase()}
                        </Badge>
                      </div>
                      <div className="text-3xl font-bold text-blue-600">{dataSummary.data_health?.overall_score}%</div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {dataSummary.data_health?.collections_with_data} of {dataSummary.data_health?.total_collections} collections have data
                      </p>
                    </div>
                    
                    {/* Collections Status */}
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm">Operational Data Collections</h4>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {dataSummary.operational_data && Object.entries(dataSummary.operational_data).map(([key, data]) => (
                          <div key={key} className="flex items-center justify-between p-2 rounded border">
                            <span className="truncate">{key.replace(/_/g, ' ')}</span>
                            <Badge variant={data.has_data ? 'default' : 'outline'} className="ml-2">
                              {data.count}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Configuration Status */}
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm">System Configuration</h4>
                      <div className="flex flex-wrap gap-2">
                        {dataSummary.system_configuration && Object.entries(dataSummary.system_configuration).map(([key, data]) => (
                          <Badge key={key} variant={data.configured ? 'default' : 'outline'}>
                            {key}: {data.source}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    
                    <Button variant="outline" size="sm" onClick={fetchDataSummary} className="w-full">
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Refresh Summary
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Backup Actions Card */}
            <Card>
              <CardHeader>
                <SectionHeader 
                  icon={Database} 
                  title="Backup & Restore" 
                  description="Export and import system data"
                />
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Export Section */}
                <div className="p-4 border rounded-lg space-y-3">
                  <div className="flex items-center gap-2">
                    <Download className="h-5 w-5 text-green-600" />
                    <h4 className="font-medium">Export Data</h4>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Download a complete backup of all system configuration and operational data as JSON.
                  </p>
                  <Button 
                    onClick={exportAllData} 
                    disabled={exporting}
                    className="w-full bg-green-600 hover:bg-green-700"
                    data-testid="export-data-btn"
                  >
                    {exporting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Exporting...
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4 mr-2" />
                        Export All Data
                      </>
                    )}
                  </Button>
                </div>
                
                {/* Import Section */}
                {isSuperAdmin && (
                  <div className="p-4 border rounded-lg space-y-3">
                    <div className="flex items-center gap-2">
                      <Upload className="h-5 w-5 text-amber-600" />
                      <h4 className="font-medium">Import Data</h4>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Restore data from a previous backup. This will replace all existing data.
                    </p>
                    <div className="relative">
                      <input
                        type="file"
                        accept=".json"
                        onChange={handleImportFile}
                        disabled={importing}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        data-testid="import-file-input"
                      />
                      <Button 
                        variant="outline" 
                        className="w-full border-amber-300 text-amber-600 hover:bg-amber-50"
                        disabled={importing}
                      >
                        {importing ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Importing...
                          </>
                        ) : (
                          <>
                            <Upload className="h-4 w-4 mr-2" />
                            Select Backup File
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Seed Production Data */}
                {canEdit && (
                  <div className="p-4 border rounded-lg space-y-3">
                    <div className="flex items-center gap-2">
                      <FileJson className="h-5 w-5 text-blue-600" />
                      <h4 className="font-medium">Initialize Production Data</h4>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Seed empty collections with baseline production data. Only affects empty collections.
                    </p>
                    <Button 
                      variant="outline"
                      onClick={seedProductionData} 
                      disabled={seeding}
                      className="w-full"
                      data-testid="seed-production-btn"
                    >
                      {seeding ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Seeding...
                        </>
                      ) : (
                        <>
                          <FileJson className="h-4 w-4 mr-2" />
                          Seed Production Data
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Theme & Appearance */}
        <TabsContent value="theme">
          <Card>
            <CardHeader>
              <SectionHeader 
                icon={Palette} 
                title="Theme & Appearance" 
                description="Customize the look and feel of CareLens"
              />
            </CardHeader>
            <CardContent className="space-y-8">
              {/* Dark/Light Mode */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold flex items-center gap-2">
                  {theme === 'dark' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
                  Display Mode
                </h4>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { id: 'light', name: 'Light', icon: Sun },
                    { id: 'dark', name: 'Dark', icon: Moon },
                    { id: 'system', name: 'System', icon: Settings }
                  ].map(mode => (
                    <button
                      key={mode.id}
                      onClick={() => setTheme(mode.id)}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        theme === mode.id 
                          ? 'border-primary bg-primary/10' 
                          : 'border-border hover:border-primary/50'
                      }`}
                      data-testid={`theme-${mode.id}`}
                    >
                      <mode.icon className={`h-6 w-6 mx-auto mb-2 ${theme === mode.id ? 'text-primary' : 'text-muted-foreground'}`} />
                      <span className={`text-sm font-medium ${theme === mode.id ? 'text-primary' : ''}`}>
                        {mode.name}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Color Scheme */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold flex items-center gap-2">
                  <Palette className="h-4 w-4" />
                  Color Scheme
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {(colorSchemes || COLOR_SCHEMES).map(scheme => (
                    <button
                      key={scheme.id}
                      onClick={() => setColorScheme(scheme.id)}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        colorScheme === scheme.id 
                          ? 'border-primary bg-primary/10' 
                          : 'border-border hover:border-primary/50'
                      }`}
                      data-testid={`color-scheme-${scheme.id}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <div className={`w-4 h-4 rounded-full ${
                          scheme.id === 'default' ? 'bg-slate-800 dark:bg-teal-500' :
                          scheme.id === 'ocean' ? 'bg-sky-500' :
                          scheme.id === 'emerald' ? 'bg-emerald-500' :
                          scheme.id === 'sunset' ? 'bg-rose-500' :
                          scheme.id === 'midnight' ? 'bg-violet-500' :
                          scheme.id === 'coral' ? 'bg-pink-500' : 'bg-slate-500'
                        }`} />
                        <span className={`font-medium ${colorScheme === scheme.id ? 'text-primary' : ''}`}>
                          {scheme.name}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">{scheme.description}</p>
                      {colorScheme === scheme.id && (
                        <Badge className="mt-2" variant="secondary">Active</Badge>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Font Size - Future feature placeholder */}
              <div className="space-y-4 opacity-60">
                <h4 className="text-sm font-semibold flex items-center gap-2">
                  Font Size (Coming Soon)
                </h4>
                <p className="text-sm text-muted-foreground">
                  Adjust text size for better readability. This feature will be available in a future update.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit Log */}
        <TabsContent value="audit">
          <Card>
            <CardHeader>
              <SectionHeader 
                icon={History} 
                title="Configuration Audit Log" 
                description="History of all configuration changes"
              />
            </CardHeader>
            <CardContent>
              {auditHistory.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <History className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No configuration changes recorded yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {auditHistory.map((entry, idx) => (
                    <div key={idx} className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <Badge variant="outline">{entry.config_type}</Badge>
                        <span className="text-sm text-muted-foreground">
                          {new Date(entry.changed_at).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-sm">
                        Changed by: <strong>{entry.changed_by_email}</strong>
                      </p>
                      <div className="mt-2 text-xs bg-muted p-2 rounded overflow-auto max-h-32">
                        <pre>{JSON.stringify(entry.new_values, null, 2)}</pre>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
